import { useEffect, useCallback, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { z } from 'zod'

import { logError } from '~publish/legacy/utils/logError'

const successMessage = z.object({
  type: z.literal('canva-return-success'),
  uid: z.string(),
})

interface UseCanvaLoader {
  showCanvaOriginalPostClosedModal: boolean
}

export const useCanvaLoader = (): UseCanvaLoader => {
  const [searchParams] = useSearchParams()
  const [
    showCanvaOriginalPostClosedModal,
    setShowCanvaOriginalPostClosedModal,
  ] = useState(false)

  const canvaReturnParam = useMemo(() => {
    return searchParams.get('canva_return_mode')
  }, [searchParams])

  /**
   * Global handler for canva return mode -
   * this is used to send a message to the main window to trigger the upload of the canva design
   * @param payload - the payload containing the export url, design id, and correlation state
   */
  const canvaReturnHandler = useCallback(
    (payload: {
      exportUrl: string
      designId: string
      correlationState: string
    }): void => {
      const channel = `canva-return-${atob(payload.correlationState)}`
      const broadcaster = new BroadcastChannel(channel)
      const message = {
        type: 'canva-return',
        designId: payload.designId,
        uid: payload.correlationState,
        exportUrl: payload.exportUrl,
      }
      broadcaster.addEventListener('message', (event): void => {
        // when the main window receives a message from the canva return tab, it will close the tab
        const data = successMessage.safeParse(event.data)
        if (data.success && data.data.uid === payload.correlationState) {
          window.close()
        }
      })
      // BroadcastChannel is restricted to same-origin contexts and has no targetOrigin argument.
      // oxlint-disable-next-line unicorn/require-post-message-target-origin
      broadcaster.postMessage(message)
      setTimeout(() => setShowCanvaOriginalPostClosedModal(true), 2_000)
    },
    [setShowCanvaOriginalPostClosedModal],
  )

  useEffect(() => {
    // Only process if ALL required Canva params are present
    // This prevents errors when only canva_return_mode is present (e.g., carried forward during navigation)
    const exportUrl = searchParams.get('canva_export_url')
    const designId = searchParams.get('canva_design_id')
    const correlationState = searchParams.get('canva_correlation_state')

    if (!canvaReturnParam || !exportUrl || !designId || !correlationState) {
      return
    }

    try {
      canvaReturnHandler({ exportUrl, designId, correlationState })
    } catch (error) {
      logError(error, { context: 'canvaReturnHandler' })
    }
  }, [canvaReturnParam, searchParams, canvaReturnHandler])

  return { showCanvaOriginalPostClosedModal }
}
